import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LegalShell = ({ title, updated, testid, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-zinc-900 min-h-screen" data-testid={testid}>
      <header className="border-b border-zinc-200/80">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="legal-logo"
            className="flex items-center gap-2.5 font-bold text-lg tracking-tight text-zinc-900"
          >
            <span className="w-3.5 h-3.5 bg-zinc-900 rounded-[4px]" aria-hidden="true" />
            Naveli
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Tilbage til forsiden
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">{title}</h1>
        <p className="mt-3 text-sm text-zinc-500">Senest opdateret: {updated}</p>
        <div className="mt-10 space-y-9 text-base text-zinc-600 leading-relaxed">{children}</div>
      </main>
      <footer className="border-t border-zinc-100 py-8 text-center text-xs text-zinc-400">
        © 2026 Naveli. Alle rettigheder forbeholdes.
      </footer>
    </div>
  );
};

const H = ({ children }) => (
  <h2 className="text-xl font-semibold text-zinc-900 mb-3">{children}</h2>
);

export const Privatlivspolitik = () => (
  <LegalShell title="Privatlivspolitik" updated="september 2026" testid="privacy-page">
    <section>
      <H>1. Dataansvarlig</H>
      <p>
        Naveli er dataansvarlig for behandlingen af de personoplysninger, du afgiver
        via denne hjemmeside. Du kan altid kontakte os på{" "}
        <a href="mailto:naveli.dk@gmail.com" className="underline underline-offset-2 hover:text-zinc-900">
          naveli.dk@gmail.com
        </a>{" "}
        eller telefon 22 66 46 05 / 42 17 32 09. CVR-nummer er under registrering.
      </p>
    </section>
    <section>
      <H>2. Hvilke oplysninger indsamler vi?</H>
      <p>
        Når du udfylder kontaktformularen, indsamler vi de oplysninger, du selv
        indtaster: navn, virksomhedsnavn, e-mailadresse, telefonnummer, om du har en
        eksisterende hjemmeside, samt din besked om projektet.
      </p>
    </section>
    <section>
      <H>3. Formål</H>
      <p>
        Vi bruger udelukkende oplysningerne til at besvare din henvendelse,
        udarbejde et gratis og uforpligtende forslag samt følge op på vores dialog.
        Oplysningerne bruges ikke til markedsføring uden dit samtykke.
      </p>
    </section>
    <section>
      <H>4. Retsgrundlag</H>
      <p>
        Behandlingen sker med hjemmel i GDPR artikel 6, stk. 1, litra b (foranstaltninger
        der træffes forud for en eventuel kontrakt) og litra f (vores legitime
        interesse i at kunne besvare henvendelser fra potentielle kunder).
      </p>
    </section>
    <section>
      <H>5. Opbevaring</H>
      <p>
        Vi opbevarer dine oplysninger kun så længe, det er nødvendigt for at behandle
        din henvendelse. Henvendelser, der ikke fører til et samarbejde, slettes
        typisk senest 12 måneder efter seneste kontakt.
      </p>
    </section>
    <section>
      <H>6. Videregivelse</H>
      <p>
        Vi sælger eller videregiver aldrig dine oplysninger til tredjemand. Oplysninger
        behandles alene af os og af de tekniske driftleverandører (hosting og
        e-mail), der er nødvendige for at drive hjemmesiden, og som behandler data
        efter vores instruks.
      </p>
    </section>
    <section>
      <H>7. Dine rettigheder</H>
      <p>
        Du har ret til indsigt, berigtigelse, sletning, begrænsning af behandling,
        indsigelse samt dataportabilitet. Skriv til os på{" "}
        <a href="mailto:naveli.dk@gmail.com" className="underline underline-offset-2 hover:text-zinc-900">
          naveli.dk@gmail.com
        </a>
        , så hjælper vi dig. Du kan altid klage til Datatilsynet, hvis du mener, at
        vi behandler dine oplysninger i strid med reglerne.
      </p>
    </section>
    <section>
      <H>8. Ændringer</H>
      <p>
        Vi kan opdatere denne privatlivspolitik, når det er nødvendigt. Den aktuelle
        version er altid tilgængelig på denne side.
      </p>
    </section>
  </LegalShell>
);

export const Cookiepolitik = () => (
  <LegalShell title="Cookiepolitik" updated="september 2026" testid="cookie-page">
    <section>
      <H>1. Hvad er cookies?</H>
      <p>
        Cookies er små tekstfiler, der gemmes på din enhed, når du besøger en
        hjemmeside. De bruges typisk til at få siden til at fungere, huske dine
        præferencer eller indsamle statistik.
      </p>
    </section>
    <section>
      <H>2. Hvilke cookies bruger Naveli?</H>
      <p>
        Navelis hjemmeside bruger kun teknisk nødvendige cookies, der er påkrævet for
        at vise siden korrekt. Vi bruger ikke cookies til markedsføring,
        sporing eller statistik, og vi deler ingen oplysninger om dit besøg med
        annoncører.
      </p>
    </section>
    <section>
      <H>3. Tredjepartsindhold</H>
      <p>
        Vores koncepteksempler kan indeholde indlejrede kort (Google Maps). Når det
        indhold vises, kan den pågældende tredjepart sætte cookies efter deres egne
        politikker. Det gælder kun på konceptsiderne og ikke på vores hovedside.
      </p>
    </section>
    <section>
      <H>4. Sådan administrerer du cookies</H>
      <p>
        Du kan til enhver tid slette eller blokere cookies i din browsers
        indstillinger. Fremgangsmåden finder du i hjælpemenuen i din browser (fx
        Chrome, Safari, Firefox eller Edge). Bemærk, at blokering af nødvendige
        cookies kan påvirke, hvordan siden vises.
      </p>
    </section>
    <section>
      <H>5. Spørgsmål</H>
      <p>
        Har du spørgsmål til vores brug af cookies, er du velkommen til at skrive til{" "}
        <a href="mailto:naveli.dk@gmail.com" className="underline underline-offset-2 hover:text-zinc-900">
          naveli.dk@gmail.com
        </a>
        .
      </p>
    </section>
  </LegalShell>
);
